import { useState } from 'react'
import { Alert, Badge, Button, Group, Loader, Modal, Stack, Table, Text, TextInput, Title } from '@mantine/core'
import { IconKey, IconLink, IconPlus, IconUnlink } from '@tabler/icons-react'
import type { ChaveDaOrg, GatewayWhatsApp, OrgComAcesso } from '@/api/whatsappGateways'
import {
  useApontarChave, useArquivosDaBaseDaChave, useAtivarChave, useChavesDaOrg, useCriarChave,
  useDesapontarChave, useDesativarChave, useExcluirChave, useRotacionarChave,
} from '@/hooks/useWhatsappGateways'
import { ChaveRevelada } from './ChaveRevelada'
import { mensagemDeErro } from './mensagens'

type AcaoConfirmada = 'rotacionar' | 'desativar' | 'excluir'

const CONFIRMACAO: Record<AcaoConfirmada, { titulo: string; texto: (nome: string) => string; botao: string }> = {
  rotacionar: {
    titulo: 'Rotacionar a chave?',
    texto: (n) => `A chave "${n}" atual para de funcionar na hora. Quem a usa precisa receber a nova, que aparece uma única vez.`,
    botao: 'Rotacionar',
  },
  desativar: {
    titulo: 'Desativar a chave?',
    texto: (n) => `Envios com a chave "${n}" passam a ser recusados até ela ser ativada de novo.`,
    botao: 'Desativar',
  },
  excluir: {
    titulo: 'Excluir a chave?',
    texto: (n) => `A chave "${n}" é apagada no WhatsApi e não tem volta. Para só suspender, use Desativar.`,
    botao: 'Excluir',
  },
}

/**
 * O que a exclusão leva junto: a Base de Conhecimento da chave é apagada com ela (CASCADE
 * no WhatsApi). Conta os arquivos pela rota da organização; sem resposta (erro ou sem
 * `whatsapi:read` na organização), o aviso é genérico — nunca some.
 */
function AvisoDaBaseDaChave({ orgId, keyId }: { orgId: number; keyId: string }) {
  const kb = useArquivosDaBaseDaChave(orgId, keyId)
  if (kb.isLoading) return <Text size="sm" c="dimmed">Contando os arquivos da Base de Conhecimento desta chave…</Text>
  if (!kb.data) {
    return (
      <Alert color="yellow" variant="light">
        A Base de Conhecimento desta chave também é apagada, com todos os arquivos dela.
      </Alert>
    )
  }
  const n = kb.data.length
  if (n === 0) return <Text size="sm" c="dimmed">A Base de Conhecimento desta chave não tem arquivos.</Text>
  return (
    <Alert color="yellow" variant="light">
      Apaga também {n} {n === 1 ? 'arquivo' : 'arquivos'} da Base de Conhecimento desta chave.
    </Alert>
  )
}

/**
 * Chaves de API da organização no WhatsApi do ECS e para qual gateway cada uma aponta
 * (spec 2026-09-23 §4.1; ciclo de vida na spec 2026-10-16-whatsapi-chaves-no-ecs).
 *
 * Só gateway provisionado recebe chave nova ou apontada; desapontar vale sempre (é o que
 * libera a exclusão do gateway). Rotacionar, ativar/desativar e excluir só valem para
 * chave que aponta para ESTE gateway — a trilha é por gateway.
 *
 * A key em texto (criar/rotacionar) vive só no `data` da mutação e no modal: fechar o
 * modal chama `reset()` e ela some. Nunca vai para uma query, que o app persiste.
 * A lista nunca lê o item devolvido pelas mutações — a tela relê a lista completa.
 */
export function ChavesDoGateway({ org, gateway }: { org: OrgComAcesso; gateway: GatewayWhatsApp }) {
  const chaves = useChavesDaOrg(org.id)
  const apontar = useApontarChave(org.id, gateway.id)
  const desapontar = useDesapontarChave(org.id, gateway.id)
  const criar = useCriarChave(org.id, gateway.id)
  const rotacionar = useRotacionarChave(org.id, gateway.id)
  const ativar = useAtivarChave(org.id, gateway.id)
  const desativar = useDesativarChave(org.id, gateway.id)
  const excluir = useExcluirChave(org.id, gateway.id)
  const [novaAberta, setNovaAberta] = useState(false)
  const [nome, setNome] = useState('')
  const [confirmar, setConfirmar] = useState<{ acao: AcaoConfirmada; chave: ChaveDaOrg } | null>(null)
  const pronto = gateway.status === 'provisionado'
  // Ciclo de vida da chave: `manage_keys` (que o backend já devolve como "manage_keys OU
  // manage"). Apontar/desapontar seguem em `manage` (spec 2026-10-16 §4.1).
  const gereChaves = Boolean(org.pode.manage_keys)
  const temAcao = org.pode.manage || gereChaves
  const itens = chaves.data ?? []
  const segredo = criar.data?.key ?? rotacionar.data?.key ?? null
  const nomeDoSegredo = criar.data?.item.name ?? rotacionar.data?.item.name ?? ''

  function fecharSegredo() {
    criar.reset()
    rotacionar.reset()
  }

  function enviarNova() {
    const n = nome.trim()
    if (!n) return
    criar.mutate(n, {
      onSuccess: () => {
        setNovaAberta(false)
        setNome('')
      },
    })
  }

  function confirmarAcao() {
    if (!confirmar) return
    const { acao, chave } = confirmar
    const fim = { onSettled: () => setConfirmar(null) }
    if (acao === 'rotacionar') rotacionar.mutate(chave.id, fim)
    else if (acao === 'desativar') desativar.mutate(chave.id, fim)
    else excluir.mutate(chave.id, fim)
  }

  const ocupado = (m: { isPending: boolean; variables?: string }, id: string) => m.isPending && m.variables === id

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={4}>Chaves de API</Title>
        {gereChaves && pronto && (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setNovaAberta(true)}>
            Nova chave
          </Button>
        )}
      </Group>
      {chaves.isLoading ? (
        <Loader size="sm" />
      ) : chaves.isError ? (
        <Alert color="red" variant="light">{mensagemDeErro(chaves.error)}</Alert>
      ) : itens.length === 0 ? (
        <Text size="sm" c="dimmed">
          Nenhuma chave de API desta organização no WhatsApi. Chave sem organização gravada não aparece aqui.
        </Text>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Chave</Table.Th>
              <Table.Th>Prefixo</Table.Th>
              <Table.Th>Situação</Table.Th>
              <Table.Th>Aponta para</Table.Th>
              {temAcao && <Table.Th>Ação</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {itens.map((k) => {
              const aqui = k.gateway_id === gateway.id
              return (
                <Table.Tr key={k.id}>
                  <Table.Td>{k.name}</Table.Td>
                  <Table.Td><Text ff="monospace" size="xs">{k.key_prefix}</Text></Table.Td>
                  <Table.Td>
                    <Badge size="sm" variant="light" color={k.is_active ? 'teal' : 'gray'}>
                      {k.is_active ? 'Ativa' : 'Desativada'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {aqui ? (
                      <Text c="green.6" fw={600} size="sm">Este gateway</Text>
                    ) : k.gateway_id ? (
                      <Text ff="monospace" size="xs" c="dimmed">{k.gateway_id}</Text>
                    ) : k.gateway_legado ? (
                      <Text c="yellow.6" size="sm">{`Legado: ${k.gateway_legado}`}</Text>
                    ) : (
                      <Text c="dimmed" size="sm">Nenhum</Text>
                    )}
                  </Table.Td>
                  {temAcao && (
                    <Table.Td>
                      {aqui ? (
                        <Group gap={4} wrap="wrap">
                          {gereChaves && (
                            <Button size="xs" variant="subtle" leftSection={<IconKey size={14} />}
                              loading={ocupado(rotacionar, k.id)}
                              onClick={() => setConfirmar({ acao: 'rotacionar', chave: k })}>
                              Rotacionar
                            </Button>
                          )}
                          {gereChaves && (k.is_active ? (
                            <Button size="xs" variant="subtle" color="gray" loading={ocupado(desativar, k.id)}
                              onClick={() => setConfirmar({ acao: 'desativar', chave: k })}>
                              Desativar
                            </Button>
                          ) : (
                            <Button size="xs" variant="subtle" loading={ocupado(ativar, k.id)}
                              onClick={() => ativar.mutate(k.id)}>
                              Ativar
                            </Button>
                          ))}
                          {org.pode.manage && (
                            <Button size="xs" variant="subtle" color="red" leftSection={<IconUnlink size={14} />}
                              loading={ocupado(desapontar, k.id)} onClick={() => desapontar.mutate(k.id)}>
                              Desapontar
                            </Button>
                          )}
                          {gereChaves && (
                            <Button size="xs" variant="subtle" color="red" loading={ocupado(excluir, k.id)}
                              onClick={() => setConfirmar({ acao: 'excluir', chave: k })}>
                              Excluir
                            </Button>
                          )}
                        </Group>
                      ) : org.pode.manage ? (
                        <Button
                          size="xs"
                          variant="outline"
                          leftSection={<IconLink size={14} />}
                          disabled={!pronto}
                          loading={ocupado(apontar, k.id)}
                          onClick={() => apontar.mutate(k.id)}
                        >
                          Apontar para este gateway
                        </Button>
                      ) : null}
                    </Table.Td>
                  )}
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      )}
      {temAcao && !pronto && (
        <Text size="xs" c="dimmed">Só gateway provisionado recebe chave.</Text>
      )}

      <Modal opened={novaAberta} onClose={() => setNovaAberta(false)} title="Nova chave de API">
        <Stack>
          <Text size="sm">
            A chave nasce apontada para este gateway e envia pelo número dele. Ela aparece uma única vez, logo depois de criada.
          </Text>
          <TextInput
            label="Nome"
            description="De 1 a 64 caracteres: letras, números, espaço, ponto, hífen ou sublinhado. Único no WhatsApi."
            value={nome}
            maxLength={64}
            onChange={(e) => setNome(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') enviarNova() }}
            data-autofocus
          />
          <Group>
            <Button variant="subtle" onClick={() => setNovaAberta(false)}>Cancelar</Button>
            <Button disabled={!nome.trim()} loading={criar.isPending} onClick={enviarNova}>Criar chave</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={confirmar != null} onClose={() => setConfirmar(null)} title={confirmar ? CONFIRMACAO[confirmar.acao].titulo : ''}>
        <Stack>
          <Text size="sm">{confirmar ? CONFIRMACAO[confirmar.acao].texto(confirmar.chave.name) : ''}</Text>
          {confirmar?.acao === 'excluir' && <AvisoDaBaseDaChave orgId={org.id} keyId={confirmar.chave.id} />}
          <Group>
            <Button variant="subtle" onClick={() => setConfirmar(null)}>Cancelar</Button>
            <Button
              color={confirmar?.acao === 'rotacionar' ? undefined : 'red'}
              loading={rotacionar.isPending || desativar.isPending || excluir.isPending}
              onClick={confirmarAcao}
            >
              {confirmar ? CONFIRMACAO[confirmar.acao].botao : ''}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ChaveRevelada segredo={segredo} nome={nomeDoSegredo} onClose={fecharSegredo} />
    </Stack>
  )
}
